import { Button, Form, FormProps, Input, message, notification } from "antd";
import { Credentials } from "./Login.types";
import { LoginService } from "../../services/LoginService";
import ClientService from "../../../ClientView/services/ClientService";
import { usePartnershipContext } from "../../../../core/context/PartnershipContext";
import { useMsal } from "@azure/msal-react";

import "./Login.css";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

const loginClientService = new ClientService();

export const Login = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { setSelectedClient, setSelectedPartnership } = usePartnershipContext();
  const { instance } = useMsal();

  const handleSSOLogin = async () => {
    try {
      // NOTE: do NOT include the API scope here — requesting it in loginRequest
      // causes consent errors; the API token is acquired separately per-request
      // by the axios interceptor (ApiService.ts).
      await instance.loginRedirect({
        scopes: ["User.Read", "openid", "profile"],
      });
    } catch (error) {
      notification.error({
        message: "Sign-in failed",
        description: "Could not sign in with Microsoft. Please try again.",
      });
    }
  };

  const onFinish: FormProps<Credentials>["onFinish"] = async (values) => {
    try {
      setIsLoading(true);
      await LoginService(values);

      // Auto-select the Northstar demo client (or first client) so all screens
      // start with real data without requiring a manual client selection.
      try {
        const clients = await loginClientService.getAllClients();
        if (clients.length > 0) {
          const demo =
            clients.find((c) => c.name.toLowerCase().includes("northstar")) ??
            clients[0];
          setSelectedClient({ id: demo.id, name: demo.name });
          setSelectedPartnership(null);
        }
      } catch {}

      message.success("Login Successful");
      navigate("/clients");
    } catch (error) {
      notification.error({
        message: "Invalid Credentials",
        description: "Invalid username or password",
      });
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-panel">

        {/* ── Identity header bar — logo mark + version only; "Calc Engine"
             wordmark lives in the hero panel on the right ── */}
        <div className="login-header">
          <img
            src="/propel-logo-white.png"
            alt="Propel"
            className="login-header-logo"
          />
          <span className="login-header-right">
            <span className="login-header-status-dot" />
            v0.1
          </span>
        </div>

        {/* ── Form section ── */}
        <div className="login-form-wrapper">
          <Form
            name="login"
            onFinish={onFinish}
            layout="vertical"
            autoComplete="off"
          >
            <h1 className="login-heading">Sign in</h1>
            <p className="login-subtext">
              Welcome back — enter your credentials to continue.
            </p>

            <div className="field-group">
              <label className="input-label">Email Address</label>
              <Form.Item
                name="userNameValue"
                rules={[{ required: true, message: "" }]}
              >
                <Input placeholder="you@firm.com" />
              </Form.Item>
            </div>

            <div className="field-group">
              <label className="input-label">Password</label>
              <Form.Item
                name="userSecretValue"
                rules={[{ required: true, message: "" }]}
              >
                <Input.Password placeholder="••••••••" />
              </Form.Item>
            </div>

            <div className="forgotPassword">Forgot Password?</div>

            <Form.Item className="login-submit-item">
              <Button
                id="login-button"
                type="primary"
                htmlType="submit"
                loading={isLoading}
              >
                Sign in
              </Button>
            </Form.Item>
          </Form>

          <div className="login-sso-divider">
            <span>— or —</span>
          </div>

          <Button
            id="sso-login-button"
            className="login-sso-button"
            onClick={handleSSOLogin}
            icon={
              <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
              </svg>
            }
          >
            Sign in with Microsoft
          </Button>
        </div>
      </div>

      {/* ── Branding panel ── */}
      <div className="branding-panel">
        <img
          src="/propel-logo-white.png"
          alt="Propel"
          className="brand-logo"
        />
        <div className="brand-title">Calc Engine</div>
        <div className="brand-subtitle">
          Building Calculation Infrastructure for Growing Firms
        </div>
      </div>
    </div>
  );
};
